import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types
export interface Scenario {
  id: number;
  type: 'use_case' | 'call_recording' | 'transcript';
  title: string;
  content: string | string[];
  description: string;
}

export interface EnhanceSystemPromptState {
  templatePrompt: string;
  scenarios: Scenario[];
  activeTab: number;
  rawEnhancedPrompt: string;
  isSubmitting: boolean;
  isProcessing: boolean;
  hasResult: boolean;
  error: string | null;
  successMessage: string | null;
  processingAgentId: string | null;
}

const initialState: EnhanceSystemPromptState = {
  templatePrompt: '',
  scenarios: [],
  activeTab: 0,
  rawEnhancedPrompt: '',
  isSubmitting: false,
  isProcessing: false,
  hasResult: false,
  error: null,
  successMessage: null,
  processingAgentId: null,
};

const enhanceSystemPromptSlice = createSlice({
  name: 'enhanceSystemPrompt',
  initialState,
  reducers: {
    // Template Prompt
    setTemplatePrompt: (state, action: PayloadAction<string>) => {
      state.templatePrompt = action.payload;
    },

    // Scenarios
    setScenarios: (state, action: PayloadAction<Scenario[]>) => {
      state.scenarios = action.payload;
    },
    addScenario: (state, action: PayloadAction<Scenario>) => {
      state.scenarios.push(action.payload);
    },
    updateScenario: (
      state,
      action: PayloadAction<{ index: number; scenario: Scenario }>
    ) => {
      const { index, scenario } = action.payload;
      if (index >= 0 && index < state.scenarios.length) {
        state.scenarios[index] = scenario;
      }
    },
    removeScenario: (state, action: PayloadAction<number>) => {
      state.scenarios.splice(action.payload, 1);
    },

    // Active Tab
    setActiveTab: (state, action: PayloadAction<number>) => {
      state.activeTab = action.payload;
    },

    // Enhanced Prompt Result
    setRawEnhancedPrompt: (state, action: PayloadAction<string>) => {
      state.rawEnhancedPrompt = action.payload;
      state.hasResult = true;
    },

    // API Status
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    setIsProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },

    // Messages
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    clearMessages: state => {
      state.error = null;
      state.successMessage = null;
    },

    // Process Start
    startProcessing: (state, action: PayloadAction<string>) => {
      state.isProcessing = true;
      state.isSubmitting = true;
      state.error = null;
      state.successMessage = null;
      state.processingAgentId = action.payload;
    },

    // Process Success
    processSuccess: (state, action: PayloadAction<string>) => {
      state.isProcessing = false;
      state.isSubmitting = false;
      state.rawEnhancedPrompt = action.payload;
      state.hasResult = true;
      state.activeTab = 1; // Switch to Result tab
      state.successMessage =
        'Prompt optimization completed successfully! Check the Result tab.';
      state.error = null;
      state.processingAgentId = null;
    },

    // Process Error
    processError: (state, action: PayloadAction<string>) => {
      state.isProcessing = false;
      state.isSubmitting = false;
      state.error = action.payload;
      state.successMessage = null;
      state.processingAgentId = null;
    },

    // Reset State
    resetState: () => initialState,

    // Clear Result
    clearResult: state => {
      state.rawEnhancedPrompt = '';
      state.hasResult = false;
      state.activeTab = 0;
      state.successMessage = null;
      state.error = null;
    },
  },
});

export const {
  setTemplatePrompt,
  setScenarios,
  addScenario,
  updateScenario,
  removeScenario,
  setActiveTab,
  setRawEnhancedPrompt,
  setIsSubmitting,
  setIsProcessing,
  setError,
  setSuccessMessage,
  clearMessages,
  startProcessing,
  processSuccess,
  processError,
  resetState,
  clearResult,
} = enhanceSystemPromptSlice.actions;

export default enhanceSystemPromptSlice.reducer;
